import { useRef } from 'react';
import { celebrationFigureProps, useSessionCelebration } from '@savvy-kit/foundations/motion/celebration-session';
import { GainNumber } from '@savvy-kit/foundations/motion/gain-number';
import { DELAY_HEADLINE, INTENSITY_HEADLINE } from '@savvy-kit/foundations/motion/tokens';
import { isNil } from 'es-toolkit';

import { EM_DASH, safeMoney } from './utils';

type HeadlineFigureProps = {
  readonly value: number | null;
  /** What the selected range opened at; the headline counts up from here on load and on a range switch. */
  readonly from?: number | null;
  /** Re-fires the choreography when it changes, since today's balance is the same figure on every range. */
  readonly trigger?: string | number | boolean | null;
  /** Names this figure across visits so a rise since the client last looked can be celebrated once per session. */
  readonly celebrationKey: string | null;
  readonly intensity?: number;
  /** Added to the headline beat so the count lands after a page-level reveal fade rather than under it. */
  readonly delayOffset?: number;
  readonly className?: string;
  readonly testId?: string;
};

/**
 * A card's big money figure on the portal's choreography: it counts into place on the headline beat, and pays
 * out coins when the balance has grown since the client last saw it or crossed a round number on the way.
 */
export function HeadlineFigure({
  value,
  from,
  trigger,
  celebrationKey,
  intensity = INTENSITY_HEADLINE,
  delayOffset = 0,
  className,
  testId,
}: HeadlineFigureProps) {
  const celebration = useSessionCelebration({ key: celebrationKey, value });
  // A celebration belongs to the figure it was decided on. A range switch replays the count through `trigger`
  // over a different window, which is neither a new rise to pay coins for nor a reason to keep counting from
  // the last-seen balance instead of the new window's opening value.
  const firstTrigger = useRef(trigger);
  const onCelebratedFigure = trigger === firstTrigger.current;

  if (isNil(value)) {
    return (
      <p className={className} data-testid={testId}>
        {EM_DASH}
      </p>
    );
  }

  const figure = celebrationFigureProps(celebration, from ?? undefined, onCelebratedFigure);

  return (
    <p className={className} data-testid={testId}>
      <GainNumber
        value={value}
        from={figure.from}
        capTravel={figure.capTravel}
        coins={figure.coins}
        trigger={trigger}
        countThrough
        format={safeMoney}
        delay={DELAY_HEADLINE + delayOffset}
        intensity={intensity}
      />
    </p>
  );
}
